// English strings for departments and HR attribute mapping (PB-18-07, PD-540 – PD-546) and site
// retention overrides (PB-12-05, PD-547 – PD-552). Arabic: ar-model.ts.
export const enModel = {
  // ---------------------------------------------------------------- API messages
  'mdl.v.departmentInactive': 'This department is inactive. Choose an active department.',
  'mdl.v.departmentNameTaken': 'An active department with this name already exists here.',
  'mdl.v.departmentScope': 'HR departments apply to the whole organisation. Choose an organisation-wide department.',
  'mdl.v.hrDepartmentLinked': 'This HR department is already linked to another department.',
  'mdl.v.notSiteClass': 'This record class is kept per person or organisation, so a site cannot override it.',
  'mdl.api.departmentSourceOwned': 'This department comes from the HR system. Change it there; the next synchronisation applies it.',
  'mdl.api.hrSyncDisabled': 'HR synchronisation is switched off for this connector.',
  'mdl.api.noSiteRetention': 'This site has no published retention override.',
  'mdl.api.siteRetentionEmpty': 'The draft has no periods. To return the site to the shared rules, remove the published override instead.',
  'mdl.api.siteRetentionProblems': 'Fix the marked periods before publishing.',
  'mdl.api.sitePolicyChanged': 'The organisation retention policy changed since the preview. Review the preview again.',
  'ppl.ra.reason.host_inactive_hr': 'Left the HR system',
  // Retention class labels missing from the privacy catalogue (shown by the site retention panel).
  'pv.class.ai_records': 'AI assistance records',
  'pv.class.candidate_records': 'Candidate (HR) notes',

  // ---------------------------------------------------------------- departments
  'mdl.tab.departments': 'Departments',
  'mdl.dep.title': 'Departments',
  'mdl.dep.intro': 'Departments are shared references. Renaming one changes the name for the hosts already in it and moves nobody else. An inactive department keeps its hosts and history but cannot be chosen for new assignments.',
  'mdl.dep.none': 'No departments yet.',
  'mdl.dep.col.name': 'Department',
  'mdl.dep.col.scope': 'Applies to',
  'mdl.dep.col.source': 'Source',
  'mdl.dep.col.hosts': 'Hosts',
  'mdl.dep.col.status': 'Status',
  'mdl.dep.orgWide': 'Whole organisation',
  'mdl.dep.source.manual': 'Entered here',
  'mdl.dep.source.migrated': 'Copied from earlier free text',
  'mdl.dep.source.directory': 'Employee directory',
  'mdl.dep.source.import': 'Import',
  'mdl.dep.source.hr': 'HR system',
  'mdl.dep.hrId': 'HR id {id}',
  'mdl.dep.add': 'Add department',
  'mdl.dep.name': 'Name',
  'mdl.dep.site': 'Site',
  'mdl.dep.siteHint': 'Leave empty for a department used across the organisation.',
  'mdl.dep.reason': 'Reason',
  'mdl.dep.rename': 'Rename',
  'mdl.dep.renameTitle': 'Rename {name}',
  'mdl.dep.renameNote': 'Hosts in this department ({count}) will show the new name. No host moves to another department.',
  'mdl.dep.deactivate': 'Deactivate',
  'mdl.dep.reactivate': 'Reactivate',
  'mdl.dep.deactivateTitle': 'Deactivate {name}',
  'mdl.dep.deactivateNote': 'The {count} hosts in this department keep it and its history. It can no longer be chosen for new assignments.',
  'mdl.dep.reactivateTitle': 'Reactivate {name}',
  'mdl.dep.history': 'History',
  'mdl.dep.historyTitle': 'History of {name}',
  'mdl.dep.historyNone': 'No changes recorded.',
  'mdl.dep.kind.created': 'Created',
  'mdl.dep.kind.renamed': 'Renamed from “{old}” to “{new}”',
  'mdl.dep.kind.deactivated': 'Deactivated',
  'mdl.dep.kind.reactivated': 'Reactivated',
  'mdl.dep.kind.linked': 'Linked to the HR system',
  'mdl.dep.hostsAffected': '{count} hosts',
  'mdl.dep.byRun': 'HR synchronisation',
  'mdl.dep.hrOwned': 'Managed in the HR system',
  'mdl.dep.showInactive': 'Show inactive departments',
  'mdl.dep.pick': 'Department',
  'mdl.dep.pickNone': 'No department',
  'mdl.dep.pickHint': 'Choose from the department list. Manage the list under Users → Departments.',

  // ---------------------------------------------------------------- HR mapping
  'mdl.tab.hr': 'HR mapping',
  'mdl.hr.title': 'HR attribute mapping',
  'mdl.hr.intro': 'Choose which HR attributes Passage may read. Only mapped attributes are used; everything else in the HR record is ignored and never stored. Hosts are matched by their HR employee id, never by email.',
  'mdl.hr.none': 'No HR connector is configured. An integration operator adds and tests one under Integrations.',
  'mdl.hr.simulator': 'Development simulator — not a real HR system',
  'mdl.hr.blocked': 'Not available yet: {reason}',
  'mdl.hr.connector': 'Connector',
  'mdl.hr.connectorStatus': 'Connection',
  'mdl.hr.syncEnabled': 'Synchronisation on',
  'mdl.hr.syncDisabled': 'Synchronisation off',
  'mdl.hr.hosts': '{count} hosts ({inactive} inactive)',
  'mdl.hr.departments': '{active} departments ({inactive} inactive)',
  'mdl.hr.editMapping': 'Edit mapping',
  'mdl.hr.mappingTitle': 'Mapping for {name}',
  'mdl.hr.externalId': 'External employee id',
  'mdl.hr.externalIdNote': 'Always the HR employee id. It identifies the host and never changes.',
  'mdl.hr.field.department': 'Department (attribute holding the HR department id)',
  'mdl.hr.field.jobTitle': 'Job title',
  'mdl.hr.field.employeeNumber': 'Employee number',
  'mdl.hr.field.officeSiteCode': 'Office site code',
  'mdl.hr.fieldHint': 'HR attribute name. Leave empty to keep this field out of synchronisation.',
  'mdl.hr.enabled': 'Synchronise from this connector',
  'mdl.hr.maxDeactivation': 'Hold the run when more than this share of hosts would become inactive (%)',
  'mdl.hr.reason': 'Reason for the change',
  'mdl.hr.save': 'Save mapping',
  'mdl.hr.run': 'Synchronise now',
  'mdl.hr.runConfirm': 'Synchronise and accept the held deactivations',
  'mdl.hr.lastRun': 'Last run {when}: {outcome}',
  'mdl.hr.outcome.succeeded': 'completed',
  'mdl.hr.outcome.held': 'held for review',
  'mdl.hr.outcome.failed': 'failed',
  'mdl.hr.error': 'Reason: {code}',
  'mdl.hr.counts': '{created} added · {updated} updated · {deactivated} made inactive · {unchanged} unchanged',
  'mdl.hr.deptCounts': 'Departments: {created} added · {renamed} renamed · {deactivated} made inactive · {reactivated} reactivated',
  'mdl.hr.conflicts': 'Conflicts to resolve',
  'mdl.hr.noConflicts': 'No conflicts.',
  'mdl.hr.conflict.external': 'HR id',
  'mdl.hr.conflict.kind': 'Problem',
  'mdl.hr.conflict.action': 'Next step',
  'mdl.hr.kind.host_exists': 'A host with this email already exists and was not taken over.',
  'mdl.hr.kind.employee_number_in_use': 'The employee number is used by another host.',
  'mdl.hr.kind.duplicate_external_id': 'The id appears more than once or belongs to another source.',
  'mdl.hr.kind.invalid_entry': 'The HR record is incomplete or invalid.',
  'mdl.hr.kind.office_site_unknown': 'The office site code does not match a site.',
  'mdl.hr.kind.deactivation_threshold': 'Too many hosts would become inactive; the run was held.',
  'mdl.hr.kind.administrator_hold': 'An administrator made this host inactive; it stays inactive.',
  'mdl.hr.kind.account_linked_elsewhere': 'The account is linked to another host.',
  'mdl.hr.kind.department_unknown': 'The employee’s HR department is not mapped; the host keeps its current department.',
  'mdl.hr.kind.department_name_in_use': 'Another department already has the name “{name}”; nothing was merged.',
  'mdl.hr.kind.department_invalid': 'An HR department record is incomplete.',
  'mdl.hr.kind.other': 'Needs review.',
  'mdl.hr.linkHost': 'Link existing host',
  'mdl.hr.linkHostTitle': 'Link a host to HR employee {id}',
  'mdl.hr.linkHostNote': 'The host keeps its id, visits and history; its mapped fields will come from the HR system from the next run.',
  'mdl.hr.host': 'Host',
  'mdl.hr.linkDepartment': 'Link existing department',
  'mdl.hr.linkDepartmentTitle': 'Link HR department {id}',
  'mdl.hr.linkDepartmentNote': 'The department keeps its id and hosts; from the next run its name follows the HR system.',
  'mdl.hr.department': 'Department',
  'mdl.hr.link': 'Link',
  'mdl.hr.runs': 'Recent runs',

  // ---------------------------------------------------------------- site retention overrides
  'mdl.sr.title': 'Retention by site',
  'mdl.sr.intro': 'A site may keep some records for a different period than the shared rules, for example because of local requirements. Periods are entered by you; Passage never suggests one.',
  'mdl.sr.precedence': 'Which period applies: the site’s period for a visitor type, then the organisation’s period for that visitor type, then the site’s period for the record class, then the shared rule.',
  'mdl.sr.noPolicy': 'Publish the organisation retention rules first. Site overrides build on them.',
  'mdl.sr.site': 'Site',
  'mdl.sr.status': 'Override',
  'mdl.sr.none': 'Shared rules',
  'mdl.sr.published': 'Version {number} · {count} periods',
  'mdl.sr.draft': 'Draft {number}',
  'mdl.sr.open': 'Open',
  'mdl.sr.detailTitle': 'Retention at {site}',
  'mdl.sr.effective': 'In force at this site',
  'mdl.sr.class': 'Record class',
  'mdl.sr.type': 'Visitor type',
  'mdl.sr.allTypes': 'All visitor types',
  'mdl.sr.days': 'Period',
  'mdl.sr.source': 'From',
  'mdl.sr.source.site_visitor_type': 'This site, for the visitor type',
  'mdl.sr.source.organisation_visitor_type': 'Organisation, for the visitor type',
  'mdl.sr.source.site': 'This site',
  'mdl.sr.source.organisation': 'Shared rule',
  'mdl.sr.diff': 'Local override compared with the shared rule',
  'mdl.sr.override': 'This site',
  'mdl.sr.shared': 'Shared rule',
  'mdl.sr.draftTitle': 'Draft for this site',
  'mdl.sr.draftNone': 'No draft. Enter a period to start one.',
  'mdl.sr.daysFor': 'Days for {name}',
  'mdl.sr.clear': 'Clear',
  'mdl.sr.addType': 'Add a visitor-type period',
  'mdl.sr.typeCode': 'Visitor type code',
  'mdl.sr.saveDraft': 'Save draft',
  'mdl.sr.publish': 'Publish for this site',
  'mdl.sr.discard': 'Discard draft',
  'mdl.sr.problems': 'Problems to fix before publishing: {count}',
  'mdl.sr.problem.unknown_class': 'Unknown record class',
  'mdl.sr.problem.not_site_scoped': 'Not kept per site',
  'mdl.sr.problem.out_of_range': 'Out of range',
  'mdl.sr.problem.not_per_type': 'Not set per visitor type',
  'mdl.sr.problem.organisation_unconfigured': 'No shared rule for this class yet',
  'mdl.sr.history': 'Versions',
  'mdl.sr.basedOn': 'Published on top of organisation rules version {number}',
  'mdl.sr.statusLabel.published': 'Published',
  'mdl.sr.statusLabel.superseded': 'Replaced',
  'mdl.sr.statusLabel.discarded': 'Discarded',
  'mdl.sr.statusLabel.removed': 'Removed',
  'mdl.sr.statusLabel.draft': 'Draft',
  'mdl.sr.removedBecause': 'Removed: {reason}',
  'mdl.sr.remove': 'Remove override…',
  'mdl.sr.removeTitle': 'Remove the retention override for {site}',
  'mdl.sr.removeIntro': 'After removal these shared rules apply at this site. Nothing is deleted now; the change takes effect in the next retention plan, which a person reviews before anything is applied.',
  'mdl.sr.becomes': 'Becomes',
  'mdl.sr.records': 'Records at this site whose retention changes now',
  'mdl.sr.becomesDue': '{count} become due',
  'mdl.sr.noLongerDue': '{count} are no longer due',
  'mdl.sr.truncated': 'At least these many (the preview stops at the planning limit).',
  'mdl.sr.noRecordChange': 'No record changes its due state today.',
  'mdl.sr.removeReason': 'Reason for removal',
  'mdl.sr.confirmRemove': 'Remove override',
} as const;
